import { DynamicTerraceHub } from './DynamicTerraceHub';
import { DynamicMapStoreBase } from '../DynamicMapStoreBase';
import { DynamicProfileLayerType, ProfileKeyCode } from '../DynamicProfileEnums';
import stores from "cd-eft/models/Stores";
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import Graphic from '@arcgis/core/Graphic';
import { esriProjection, FeatureLayer, GroupLayer } from 'cd-eft/utils/esriTypes';
import { convertLinearUnit } from 'cd-eft/utils/structureLayerUtils';
import {reachHighlightSymbol,trackerSymbol,pointLabelFields2, pointLabelClass3} from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';
import { DynamicTerraceProfileStore } from './DynamicTerraceProfileStore';
import { DynamicTerraceTableStore } from './DynamicTerraceTableStore';
import { Renderers } from 'cd-eft/components/SurveyEngineering/Maps/SETMap/SETMapRenderers';

const { terraceDesignStore } = stores.AppStore;
const searchPixelDistance = 3.5;

class DynamicTerraceMapStore extends DynamicMapStoreBase {
  protected hub: DynamicTerraceHub;
  
  protected _reachStations: any[] = [];
  protected _alignmentGraphic: Graphic | undefined = void 0;
  protected _lookupDistance: number[] = [];
  protected _lookupXY: number[][] = [];
  protected _trackerGraphic: Graphic | undefined = void 0;
  protected _statinoGraphic: Graphic | undefined = void 0;
  protected _reachStationLayer: FeatureLayer | undefined = void 0;
  protected _reachStationLabelLayer: FeatureLayer | undefined = void 0;
  protected _reachStationIndex: number[] = [];

  protected viewPointerMoveHandle;

  /**
   *
   */
  constructor(hub: DynamicTerraceHub) {
    super();

    this.hub = hub;
    this.profileKeys = [ProfileKeyCode.TGS, ProfileKeyCode.REACH];
  }

  setReachStations() {
    this.clearReachStationsHandler();
    if ((terraceDesignStore.terraceDesignObject.terraceDesignStations.length > 0) &&
        (this._lookupDistance.length > 0) &&
        (this._lookupXY.length > 0)) {
      for (let i = 0; i < terraceDesignStore.terraceDesignObject.terraceDesignStations.length; i++) {
        const station = terraceDesignStore.terraceDesignObject.terraceDesignStations[i];
        if (station && station.endStation && station.channelElev) {
          const l = Number(station.endStation.replace('+', ''));
          const coordXY = this.interpolateCoords(l, this._lookupDistance, this._lookupXY);
          this._reachStations.push([coordXY[0], coordXY[1], i]);

          // const point = new Point({
          //   spatialReference: this.mapSpatialReference,
          //   x: coordXY[0],
          //   y: coordXY[1]
          // });

          // this.sketchLayer.add(new Graphic({
          //   geometry: point,
          //   symbol: profileSymbolsAndLabels.reachStationSymbol.reachStationSymbol
          // }));
        }
      }

      // Add handler to the mouse move event
      this.viewPointerMoveHandle?.remove();
      this.viewPointerMoveHandle = this.mapView?.on('pointer-move', async (e: __esri.ViewPointerMoveEvent) => {
        this.untrackAllStations();
        const point = this.mapView?.toMap({ x: e.x, y: e.y });
        if (point && this.mapView?.resolution) {
          const delta = this.mapView?.resolution * searchPixelDistance;
          const result = this._reachStations.find(s => {
            return ((Math.abs(point.x - s[0]) < delta) && (Math.abs(point.y - s[1]) < delta));
          });
          if (result) {
            const index = result[2];
            if ((index > -1) && (index < terraceDesignStore.terraceDesignObject.terraceDesignStations.length)) {
              if (this.hub.ProfileStore) {
                (this.hub.ProfileStore as DynamicTerraceProfileStore).trackStation(terraceDesignStore.terraceDesignObject.terraceDesignStations[index]);
              }
              if (this.hub.TableStore) {
                (this.hub.TableStore as DynamicTerraceTableStore).highlightRows(index);
              }
              this.trackStation(index);
            }
          } else {
            if (this.hub.ProfileStore) {
              (this.hub.ProfileStore as DynamicTerraceProfileStore).unTrackUnselectedProfilePoints();
            }
            if (this.hub.TableStore) {
              (this.hub.TableStore as DynamicTerraceTableStore).clearHighlights();
            }
          }
        }
      });
    }
  }

  clearReachStationsHandler() {
    this.viewPointerMoveHandle?.remove();
    this.viewPointerMoveHandle = undefined;
    this._reachStations = [];
  }

  async setAlignment(data: any) {
    this.clearAlignment();

    if (!this.dataSpatialReference?.equals(this.mapSpatialReference!)) {
      if (!esriProjection.isLoaded()) {
        await esriProjection.load();
      }
    }

    this._lookupDistance = data.map(point => { return point.distance; });
    const points = data.map(point => {
      return [
        point.inputPoint.x,
        point.inputPoint.y,
        convertLinearUnit(point.inputPoint.z, 'intl-feet', 'meters')
      ];
    });
    const dataLine = new Polyline({
      spatialReference: this.dataSpatialReference,
      hasZ: true,
      paths: [points]
    });
    const line = (
      this.dataSpatialReference?.equals(this.mapSpatialReference!) ?
      dataLine :
      esriProjection.project(dataLine, this.mapSpatialReference!) as Polyline
    );
    this._lookupXY = line.paths[0].map(point => { return [point[0], point[1]]; });
    // this._alignmentGraphic = new Graphic({ geometry: line, symbol: alignmentSymbol });
    // this.sketchLayer.add(this._alignmentGraphic);
  }

  clearAlignment() {
    if (this._alignmentGraphic) {
      this.sketchLayer.graphics.remove(this._alignmentGraphic);
      this._alignmentGraphic = undefined;
    }
    this._lookupDistance = [];
    this._lookupXY = [];
    this.clearTracker();
  }

  trackStation(index: number) {
    this.untrackAllStations();
    if (this._lookupDistance.length > 0 && this._lookupXY.length > 0) {
      const station = terraceDesignStore.terraceDesignObject.terraceDesignStations[index];
      if (!(station.endStation && station.channelElev)) {return};
      const l = Number(station.endStation.replace('+', ''));
      const coordXY = this.interpolateCoords(l, this._lookupDistance, this._lookupXY);
      const z = convertLinearUnit(Number(station.channelElev), 'intl-feet', 'meters');
  
      const point = new Point({
        spatialReference: this.mapSpatialReference,
        hasZ: true,
        x: coordXY[0],
        y: coordXY[1],
        z: z
      });
  
      this._statinoGraphic = new Graphic({
        geometry: point,
        attributes: { index: index },
        symbol: reachHighlightSymbol
      });
      this.sketchLayer.add(this._statinoGraphic);
    }
  }

  untrackStation(index: number) {
    if (this._statinoGraphic && this._statinoGraphic.attributes['index'] === index) {
      this.sketchLayer.graphics.remove(this._statinoGraphic);
      this._statinoGraphic = undefined;
    }
  }

  untrackAllStations() {
    if (this._statinoGraphic) {
      this.sketchLayer.graphics.remove(this._statinoGraphic);
      this._statinoGraphic = undefined;
    }
  }

  setTracker(d: number, z: number) {
    if (this._lookupDistance.length > 0 && this._lookupXY.length > 0) {
      const coordXY = this.interpolateCoords(d, this._lookupDistance, this._lookupXY);
      const point = new Point({
        spatialReference: this.mapSpatialReference,
        hasZ: true,
        x: coordXY[0],
        y: coordXY[1],
        z: convertLinearUnit(z, 'intl-feet', 'meters')
      })
  
      if (this._trackerGraphic) {
        this._trackerGraphic.geometry = point;
      } else {
        this._trackerGraphic = new Graphic({ geometry: point, symbol: trackerSymbol });
        this.sketchLayer.add(this._trackerGraphic);
      }
    }
  }
  
  clearTracker() {
    if (this._trackerGraphic) {
      this.sketchLayer.graphics.remove(this._trackerGraphic);
      this._trackerGraphic = undefined;
    }
  }

  async addStations() {
    if (this._lookupDistance.length > 0 && this._lookupXY.length > 0) {
      this.clearReachStations();
      const pointSource = [] as Graphic[];

      const stationLabels: any = [];
      this._reachStationIndex = [];
      if (terraceDesignStore.terraceDesignObject.terraceDesignStations && terraceDesignStore.terraceDesignObject.terraceDesignStations.length > 0) {
        let index = 0;
        terraceDesignStore.terraceDesignObject.terraceDesignStations.forEach((station) => {
          if (station.reachId.startsWith('R-')) {
            stationLabels.push({ 
              x: Number(station.endStation.replace('+', '')),
              y: Number(station.channelElev),
              label: `(${station.endStation}, ${station.channelElev}, ${station.cutFill})` });
              this._reachStationIndex.push(index);
          }
          index++;
        })
      }

      stationLabels.map((station, idx) => {
        const coordXY = this.interpolateCoords(station.x, this._lookupDistance, this._lookupXY);
        const point = new Graphic({
          geometry: new Point({
            spatialReference: this.mapSpatialReference,
            hasZ: true,
            x: coordXY[0],
            y: coordXY[1],
            z: convertLinearUnit(station.y, 'intl-feet', 'meters')
          }),
          attributes: {
            ObjectID: idx,
            StationInfo: station.label
          },
        });
        pointSource.push(point);
      });

      this._reachStationLayer = new FeatureLayer({
        source: pointSource,
        fields: pointLabelFields2,
        renderer: Renderers.reachStationPointRenderer as any,
        id: 'reach_stations_layer',
        title: 'Reach Stations',
        objectIdField: 'ObjectID'
      });

      this._reachStationLabelLayer = new FeatureLayer({
        source: pointSource,
        fields: pointLabelFields2,
        renderer: Renderers.alignmentStationLabelRenderer as any,
        id: 'reach_station_labels_layer',
        title: 'Reach Station Labels',
        objectIdField: 'ObjectID',
        labelingInfo: [pointLabelClass3]
      });

      if(this.mapView && this.mapView.map) {
        const groupLayer = this.mapView.map.findLayerById(`alignment${terraceDesignStore.alignmentDesignElementJsonId}`) as GroupLayer;
        if (groupLayer) {
          groupLayer.add(this._reachStationLayer);
          groupLayer.add(this._reachStationLabelLayer);
          // this._reachStationLayer = undefined;
          this._reachStationLabelLayer = undefined;
        }
      }
    }
  }

  clearReachStations() {
    this._reachStationLayer = undefined;
    this._reachStationLabelLayer = undefined;
    this._reachStationIndex = [];
    if (this.mapView && this.mapView.map) {
      const groupLayer = this.mapView.map.findLayerById(`alignment${terraceDesignStore.alignmentDesignElementJsonId}`) as GroupLayer;
      if (groupLayer) {
        let reachStationLayer = groupLayer.findLayerById('reach_stations_layer') as FeatureLayer;
        if (reachStationLayer) {
          const removedLayer = groupLayer.remove(reachStationLayer);
          if (removedLayer) removedLayer.destroy();
        }
        let reachStationLabelLayer = groupLayer.findLayerById('reach_station_labels_layer') as FeatureLayer;
        if (reachStationLabelLayer) {
          const removedLayer = groupLayer.remove(reachStationLabelLayer);
          if (removedLayer) removedLayer.destroy();
        }
      }
    }
  }

  /** abstract method implementation */
  moreSketchVmSetup() { }
  SketchPolyline() { }
  toggleAlignmentSnapping() { }
  SketchPoint() { }
  ReshapePolyline() { }
  LoadEditToMap(data: any, keyCode: ProfileKeyCode) { }
  drawCurve(curveData: any) { }
  removeMapLayer(layer: DynamicProfileLayerType) { }
  configSketchVM() { }
  updateAlignmentOnMap(data: any, keyCode: ProfileKeyCode) { }
  checkToConvertAlignment(index) { }
  redrawAlignmentProfile() { } // Only used for alignment
  generateAlignmentLabelsLayer() { }
  handlePolylineCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    return this.emptyPromiseVoid;
  }
  handlePointCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    return this.emptyPromiseVoid;
  }
  handleReshapeEvent(event: __esri.SketchViewModelUpdateEvent): Promise<void> {
    return this.emptyPromiseVoid;
  }

  Dispose() {
    this.untrackAllStations();
    this.clearTracker();
    this.clearAlignment();
    this.clearReachStations();
    this.clearReachStationsHandler();
    super.Dispose();
  }
}

export { DynamicTerraceMapStore };
